import { useCallback, useContext, useMemo, useState } from "react";

import extendedFieldsApi from "./API";
import extendedFieldsCustomFieldData from "./ExtendedFieldsManager/AddFieldSelect/customFieldData";
import {
  ExtendedFieldsContext,
  defaultFieldData,
} from "./ExtendedFieldsProvider";
import extendedFieldActions from "./actions";
import extendedFieldDataTransformers from "./dataTransformers";

const { fieldTypes } = extendedFieldDataTransformers;

/**
 * tells whether or not there is anything to display in the navigation panel.
 * @returns {boolean} hasExtendedFields - is there anything to display in the navigation panel?
 */
const useHasExtendedFields = () => {
  const items = useFieldDragItems();
  const otherGroupsfields = useOtherGroupFields();

  const hasExtendedFields = useMemo(
    () => items?.length > 0 || otherGroupsfields?.length > 0,
    [items?.length, otherGroupsfields?.length]
  );

  return hasExtendedFields;
};

const useFlatFields = () => {
  const [{ extendedFields }] = useContext(ExtendedFieldsContext);

  return useMemo(
    () => extendedFieldDataTransformers.payloadToflat(extendedFields),
    [extendedFields]
  );
};

const useFieldData = () => {
  const [state, dispatch] = useContext(ExtendedFieldsContext);

  const setFieldData = useCallback(
    (fieldProps) => {
      dispatch(extendedFieldActions.initSelectedField(fieldProps));
    },
    [dispatch]
  );

  const fieldKeys = Object.keys(defaultFieldData);
  const fieldData = Object.fromEntries(
    Object.entries(state).filter(([key]) => fieldKeys.includes(key))
  );

  return [fieldData, setFieldData];
};

const useSelectedFieldId = () => {
  const [{ networkFieldID }, dispatch] = useContext(ExtendedFieldsContext);

  const setSelectedFieldId = useCallback(
    (fieldId) => {
      dispatch(extendedFieldActions.setSelectedFieldId(fieldId));
    },
    [dispatch]
  );

  return [networkFieldID, setSelectedFieldId];
};

/**
 * Use this to refresh/set the context to the selected field.
 *
 * This is called in the useEffect in the provider to make data flow from the extendedFields slice to the rest of the context. It automatically updates the rest of the context if extendedFields is updated
 * @returns {function}
 */
const useSyncSelectedField = () => {
  const [networkFieldID] = useSelectedFieldId();
  const flatFields = useFlatFields();
  const [, setFieldData] = useFieldData();

  const syncSelectedField = useCallback(() => {
    if (networkFieldID) {
      setFieldData(flatFields[networkFieldID]);
    }
  }, [flatFields, networkFieldID, setFieldData]);

  return syncSelectedField;
};

const useSelectedFieldType = () => {
  const [{ fieldTypeID }] = useContext(ExtendedFieldsContext);

  const selectedFieldType =
    extendedFieldDataTransformers.fieldTypeIDToFieldType[fieldTypeID];

  return [selectedFieldType];
};

/**
 * returns whether or not this field is a custom field
 * @returns {[boolean]}
 */
const useSelectedIsCustom = () => {
  const [{ isCustomField }] = useContext(ExtendedFieldsContext);

  const isCustom = useMemo(() => isCustomField, [isCustomField]);

  return [isCustom];
};

/**
 * returns whether or not this field is from a parent group
 * @returns {[boolean]}
 */
const useSelectedIsAnotherGroup = () => {
  const flatFields = useFlatFields();
  const [networkFieldID] = useSelectedFieldId();

  const isAnotherGroup = flatFields[networkFieldID]?.isAnotherGroup;

  return [isAnotherGroup];
};

const useIsNavigating = () => {
  const [{ isNavigating }, dispatch] = useContext(ExtendedFieldsContext);
  const [, setErrorMessage] = useErrorMessage();

  const setIsNavigating = useCallback(
    (newIsNavigating) => {
      setErrorMessage("");
      dispatch(extendedFieldActions.setIsNavigating(newIsNavigating));
    },
    [dispatch, setErrorMessage]
  );

  return [isNavigating, setIsNavigating];
};

/**
 * Get a hook to clear the selected field from context
 * @returns {function}
 */
const useClearSelection = ({ doNotNavigate, modalShouldClear } = {}) => {
  const [, dispatch] = useContext(ExtendedFieldsContext);
  const [isModal] = useEditWithModal();
  const [, setIsNavigating] = useIsNavigating();

  const clearSelection = useCallback(() => {
    if (!isModal || modalShouldClear) {
      dispatch(extendedFieldActions.clearFieldData());
    }
    if (!doNotNavigate) setIsNavigating(true);
  }, [dispatch, doNotNavigate, isModal, modalShouldClear, setIsNavigating]);

  return clearSelection;
};
const useClearSelectionOnSave = () => {
  const [, dispatch] = useContext(ExtendedFieldsContext);
  const [, setIsNavigating] = useIsNavigating();
  const [isModal] = useEditWithModal();

  const clearSelection = () => {
    // Don't clear the field data for the modal to prevent the flash of blank data when modal closes
    if (!isModal) dispatch(extendedFieldActions.clearFieldDataOnSave());
    setIsNavigating(true); // When editWithModal is true, this will close the modal.
  };

  return clearSelection;
};

const useGroupId = () => {
  const [{ groupId }, dispatch] = useContext(ExtendedFieldsContext);

  const setGroupId = useCallback(
    (newGroupId) => {
      dispatch(extendedFieldActions.setGroupId(newGroupId));
    },
    [dispatch]
  );

  return [groupId, setGroupId];
};

const useSignupItemId = () => {
  const [{ signupItemId }, dispatch] = useContext(ExtendedFieldsContext);

  const setSignupItemId = useCallback(
    (newSignupItemId) => {
      dispatch(extendedFieldActions.setSignupItemId(newSignupItemId));
    },
    [dispatch]
  );

  return [signupItemId, setSignupItemId];
};

const useIsEveryoneGroup = () => {
  const [{ isEveryoneGroup }, dispatch] = useContext(ExtendedFieldsContext);

  const setIsEveryoneGroup = useCallback(
    (newIsEveryoneGroup) => {
      dispatch(extendedFieldActions.setIsEveryoneGroup(newIsEveryoneGroup));
    },
    [dispatch]
  );

  return [isEveryoneGroup, setIsEveryoneGroup];
};

/**
 * Returns whether or not the selected field is being used by other groups
 * @returns {[boolean]}
 */
const useSelectedIsEnabledInOtherGroups = () => {
  const [{ isEnabledInOtherGroups }] = useContext(ExtendedFieldsContext);
  return [isEnabledInOtherGroups];
};

/**
 * Returns whether or not to show other active groups
 * @returns {[boolean]}
 */
const useShowActiveOtherGroups = () => {
  const [isEnabledInOtherGroups] = useSelectedIsEnabledInOtherGroups();
  const [isEveryoneGroup] = useIsEveryoneGroup();
  const showActiveOtherGroups = isEnabledInOtherGroups && isEveryoneGroup;
  return [showActiveOtherGroups];
};

/**
 * @returns {[boolean, function]}
 */
const useInitExtendedFields = () => {
  const [{ isRefreshing }, dispatch] = useContext(ExtendedFieldsContext);
  const [groupId] = useGroupId();
  const [signupItemId] = useSignupItemId();
  const [, setErrorMessage] = useErrorMessage();
  const [, setIsEveryoneGroup] = useIsEveryoneGroup();

  const initExtendedFields = useCallback(() => {
    if (groupId || signupItemId) {
      dispatch(extendedFieldActions.setIsRefreshing(true));
      setErrorMessage("");
      return new Promise((res) => {
        extendedFieldsApi
          .getExtendedFields({ groupId, signupItemId })
          .post()
          .then((resp) => {
            const {
              customFieldData: { isEveryoneGroup, ...customFieldData },
            } = resp;
            dispatch(
              extendedFieldActions.setExtendedFields({
                ...customFieldData,
              })
            );
            setIsEveryoneGroup(isEveryoneGroup);
            res();
          })
          .catch(() => {
            setErrorMessage(
              "Something went wrong with getting the extended field data."
            );
          })
          .finally(() => {
            dispatch(extendedFieldActions.setIsRefreshing(false));
          });
      });
    }
  }, [dispatch, groupId, setErrorMessage, setIsEveryoneGroup, signupItemId]);

  return [isRefreshing, initExtendedFields];
};

const useUpdateExtendedFieldOrder = () => {
  const [groupId] = useGroupId();
  const [signupItemId] = useSignupItemId();
  const [isLoading, setIsLoading] = useState(false);
  const setNewOrder = useCallback(
    ({ orderedItems }) => {
      const newOrder = orderedItems.map(({ id }) => id);
      setIsLoading(true);
      extendedFieldsApi
        .updateExtendedFieldOrder(
          { groupId, signupItemId },
          { updatedOrder: newOrder }
        )
        .post()
        .then(() => {})
        .catch(() => {})
        .finally(() => {
          setIsLoading(false);
        });
    },
    [groupId, signupItemId]
  );

  return [isLoading, setNewOrder];
};

// These hooks retrived data for the currently selected field from flat fields since they are not to be sent to the back end and are read only.
const useIsNewField = () => {
  const flatFields = useFlatFields();
  const [networkFieldID] = useSelectedFieldId();

  const isNewField = !flatFields[networkFieldID]?.isActive;

  return [isNewField];
};

const useSelectedIsFromProfiles = () => {
  const flatFields = useFlatFields();
  const [networkFieldID] = useSelectedFieldId();

  const isFromProfiles = flatFields[networkFieldID]?.fromProfile;

  return [isFromProfiles];
};

const useSubmitField = () => {
  const [fieldData] = useFieldData();
  const [isCustom] = useSelectedIsCustom();
  const [networkFieldID] = useSelectedFieldId();
  const [groupId] = useGroupId();
  const [signupItemId] = useSignupItemId();
  const clearSelection = useClearSelectionOnSave();
  const [, initExtendedFields] = useInitExtendedFields();
  const [, setErrorMessage] = useErrorMessage();
  const [title] = useTitle();
  const [fieldType] = useSelectedFieldType();
  const [options] = useOptions();
  const [loading, setLoading] = useState(false);

  const hasNetworkFieldID = Boolean(networkFieldID);

  const endpoint = hasNetworkFieldID
    ? "enableExtendedField"
    : "createExtendedField";

  const submitField = useCallback(() => {
    if (!title) {
      setErrorMessage("Profile Field Title is required.");
      return;
    }

    if (
      fieldType === extendedFieldDataTransformers.fieldTypes.selectlist &&
      options?.length < 2
    ) {
      setErrorMessage("At least two options are required.");
      return;
    }

    setErrorMessage("");
    return new Promise((res) => {
      const payload = extendedFieldDataTransformers.getPayloadToSubmitPayload({
        groupId,
        signupItemId,
        getPayload: fieldData,
        isCustom,
      });
      setLoading(true);
      extendedFieldsApi[endpoint]({ groupId, signupItemId, payload })
        .post()
        .then(() => {
          clearSelection();
          initExtendedFields();
          res();
        })
        .catch((err) => {
          setErrorMessage("Something went wrong with saving.");
        })
        .finally(() => {
          setLoading(false);
        });
    });
  }, [
    clearSelection,
    endpoint,
    fieldData,
    fieldType,
    groupId,
    initExtendedFields,
    isCustom,
    options?.length,
    setErrorMessage,
    signupItemId,
    title,
  ]);

  return [loading, submitField];
};

/**
 * Hits the disabledExtendedField endpoint with useSelectedFieldId and the groupId. Uses passed in groupId if available.
 * @returns {[boolean, function]} - [isLoading, deleteExtendedField]
 */
const useDeleteExtendedField = (idArg) => {
  const [networkFieldID] = useSelectedFieldId();
  const [groupId] = useGroupId();
  const [signupItemId] = useSignupItemId();
  const [loading, setLoading] = useState(false);
  const [, initExtendedFields] = useInitExtendedFields();
  const [, setIsNavigating] = useIsNavigating();
  const [, setErrorMessage] = useErrorMessage();
  const clearSelection = useClearSelection();

  const deleteExtendedField = useCallback(() => {
    setLoading(true);
    setErrorMessage("");
    return new Promise((res) => {
      extendedFieldsApi
        .disabledExtendedField(
          { groupId: idArg || groupId, signupItemId: idArg || signupItemId },
          { networkFieldID }
        )
        .post()
        .then(async () => {
          // If there is a idArg, that means we are out of context and should do nothing.
          if (idArg) {
            res();
            return;
          }

          await initExtendedFields();
          setIsNavigating(true);
          clearSelection();
          res();
        })
        .catch((e) => {
          setErrorMessage("Deleting that may not have worked.");
        })
        .finally(() => {
          setLoading(false);
        });
    });
  }, [
    setErrorMessage,
    idArg,
    groupId,
    signupItemId,
    networkFieldID,
    initExtendedFields,
    setIsNavigating,
    clearSelection,
  ]);

  return [loading, deleteExtendedField];
};

const useFieldDragItems = () => {
  const [{ extendedFields }] = useContext(ExtendedFieldsContext);

  return useMemo(() => {
    const { fieldsInGroup = [], fieldsOnItem = [] } = extendedFields || {};
    const items = extendedFieldDataTransformers.rawToFieldDrag([
      ...fieldsInGroup,
      ...fieldsOnItem,
    ]);

    return items;
  }, [extendedFields]);
};

const useDefaultFields = () => {
  const [{ extendedFields }] = useContext(ExtendedFieldsContext);

  return useMemo(() => {
    const { availableFields } = extendedFields || {};
    const { defaultFields } = availableFields || {};
    return extendedFieldDataTransformers.rawToFnrOptionCards(defaultFields);
  }, [extendedFields]);
};

const useOtherGroupFields = () => {
  const [{ extendedFields }] = useContext(ExtendedFieldsContext);

  return useMemo(() => {
    const { fieldsInParentGroups = [], fieldsInEveryoneGroup = [] } =
      extendedFields || {};
    const items = extendedFieldDataTransformers.rawToFieldDrag([
      ...fieldsInEveryoneGroup,
      ...fieldsInParentGroups,
    ]);

    return items;
  }, [extendedFields]);
};

/**
 * gets the field data that is intended to all be formatted the same way
 * @returns {[object]}
 */
const useFieldSelectData = () => {
  const [{ extendedFields }] = useContext(ExtendedFieldsContext);

  return useMemo(() => {
    const { availableFields } = extendedFields || {};
    const { networkFields, thisSignup, otherSignups, profiles } =
      availableFields || {};

    return [
      {
        data: extendedFieldDataTransformers.rawToFnrOptionCards(networkFields),
        label: "Other fields in your network",
      },
      {
        data: extendedFieldDataTransformers.rawToFnrOptionCards(thisSignup),
        label: "From this Signup",
        helperText: "Another item in this Signup is using these fields.",
      },
      {
        data: extendedFieldDataTransformers.rawToFnrOptionCards(otherSignups),
        label: "From other Signups",
        helperText: "Other Signups are using these fields.",
      },
      {
        data: extendedFieldDataTransformers.rawToFnrOptionCards(profiles),
        label: "From Profiles",
        helperText:
          "These fields are from user profiles. Data already on profiles will be used if it has been entered before.",
      },
    ].filter(({ data }) => data.length > 0);
  }, [extendedFields]);
};

/**
 * Gets whether or not the user is a super admin
 * @returns {boolean}
 */
const useIsSuperAdmin = () => {
  const [{ extendedFields = {} }] = useContext(ExtendedFieldsContext);
  const { isSuperAdmin } = extendedFields;

  return useMemo(() => isSuperAdmin, [isSuperAdmin]);
};

/**
 * Gets whether or not this field is read-only
 * @returns {boolean}
 */
const useIsReadOnly = () => {
  const isSuperAdmin = useIsSuperAdmin();
  const [editPermissionID] = useEditPermissionId();

  const groupAdminsCanEdit = editPermissionID < 3;

  return useMemo(
    () => !isSuperAdmin && !groupAdminsCanEdit,
    [groupAdminsCanEdit, isSuperAdmin]
  );
};

const useIcon = () => {
  const [{ icon }, dispatch] = useContext(ExtendedFieldsContext);

  const setIcon = useCallback(
    (newIcon) => dispatch(extendedFieldActions.setIcon(newIcon)),
    [dispatch]
  );

  return [icon, setIcon];
};

const useTitle = () => {
  const [{ title }, dispatch] = useContext(ExtendedFieldsContext);

  const setTitle = useCallback(
    (newTitle) => dispatch(extendedFieldActions.setTitle(newTitle)),
    [dispatch]
  );

  return [title, setTitle];
};

const useIsUsedBySmartGroup = () => {
  const [{ isUsedBySmartGroup }] = useContext(ExtendedFieldsContext);

  return [isUsedBySmartGroup];
};

const useNotDeletable = () => {
  const [{ notDeletable }] = useContext(ExtendedFieldsContext);

  return [notDeletable];
};

/**
 * return tuple with the currect value for required field and the function to set the state slice.
 * @returns {[boolean, function]}
 */
const useIsRequiredField = () => {
  const [{ required }, dispatch] = useContext(ExtendedFieldsContext);

  const setRequired = (newRequired) =>
    dispatch(extendedFieldActions.setRequired(newRequired));

  return [required, setRequired];
};

/**
 * Retrieves the group name from the selected field.
 * @returns {[string]}
 */
const useGroupName = () => {
  const [{ groupName }] = useContext(ExtendedFieldsContext);
  return [groupName];
};

/**
 * returns the edit permissions level (1,2, or 3) and the setter
 * @returns {[number, funtion]}
 */
const useEditPermissionId = () => {
  const [
    {
      fieldResponseViewPermissionID: viewId,
      fieldResponseEditPermissionID: editId,
    },
    dispatch,
  ] = useContext(ExtendedFieldsContext);

  const setEditId = useCallback(
    (newEditId) => {
      dispatch(extendedFieldActions.setEditPermissionID(newEditId));
      if (newEditId < viewId) {
        dispatch(extendedFieldActions.setViewPermissionID(newEditId));
      }
      if (newEditId > 1) {
        dispatch(extendedFieldActions.setIig(0));
        dispatch(extendedFieldActions.setPeriodicIig(0));
        dispatch(extendedFieldActions.setShowOnSignUp(0));
      }
    },
    [dispatch, viewId]
  );
  return [editId, setEditId];
};

const useViewPermissionId = () => {
  const [
    {
      fieldResponseViewPermissionID: viewId,
      fieldResponseEditPermissionID: editId,
    },
    dispatch,
  ] = useContext(ExtendedFieldsContext);

  const setViewId = useCallback(
    (newViewId) => {
      dispatch(extendedFieldActions.setViewPermissionID(newViewId));
      if (newViewId > editId)
        dispatch(extendedFieldActions.setEditPermissionID(newViewId));
      if (newViewId > 1) {
        dispatch(extendedFieldActions.setIig(0));
        dispatch(extendedFieldActions.setPeriodicIig(0));
        dispatch(extendedFieldActions.setShowOnSignUp(0));
      }
    },
    [dispatch, editId]
  );
  return [viewId, setViewId];
};

const useShowOnSignUp = () => {
  const [{ showOnSignUp }, dispatch] = useContext(ExtendedFieldsContext);

  const setShowOnSignUp = (show) =>
    dispatch(extendedFieldActions.setShowOnSignUp(show));

  return [showOnSignUp, setShowOnSignUp];
};

const useIig = () => {
  const [{ iigOn }, dispatch] = useContext(ExtendedFieldsContext);

  const setIig = (newValue) => dispatch(extendedFieldActions.setIig(newValue));

  return [Boolean(iigOn), setIig];
};

const usePeriodicIig = () => {
  const [{ periodicIIGOn }, dispatch] = useContext(ExtendedFieldsContext);

  const setPeriodicIig = useCallback(
    (newValue) => dispatch(extendedFieldActions.setPeriodicIig(newValue)),
    [dispatch]
  );

  return [Boolean(periodicIIGOn), setPeriodicIig];
};

const useShareWith = () => {
  const [{ fieldResponseEntity }, dispatch] = useContext(ExtendedFieldsContext);

  const shareWith = useMemo(() => fieldResponseEntity, [fieldResponseEntity]);

  const setShareWith = (newValue) =>
    dispatch(extendedFieldActions.setShareWith(newValue));

  return [shareWith, setShareWith];
};

const useMultiline = () => {
  const [{ configuration }, dispatch] = useContext(ExtendedFieldsContext);
  const multiline = configuration?.behaviorOptions?.displayAs === "multi";
  const setMultiline = useCallback(
    (newValue) => dispatch(extendedFieldActions.setMultiline(newValue)),
    [dispatch]
  );

  return [multiline, setMultiline];
};

const useMin = () => {
  const [{ configuration }, dispatch] = useContext(ExtendedFieldsContext);
  const min = configuration?.responseOptions?.min;

  const setMin = (newMin) => {
    let newValue;
    if (newMin === "") {
      newValue = undefined;
    } else if (isNaN(Number(newMin))) {
      return;
    } else {
      newValue = Math.max(Math.round(Number(newMin)), 0);
    }
    dispatch(extendedFieldActions.setMin(newValue));
  };

  return [min, setMin];
};

const useMax = () => {
  const [{ configuration }, dispatch] = useContext(ExtendedFieldsContext);
  const max = configuration?.responseOptions?.max;

  const setMax = (newMax) => {
    let newValue;
    if (newMax === "") {
      newValue = undefined;
    } else if (isNaN(Number(newMax))) {
      return;
    } else {
      newValue = Math.max(Math.round(Number(newMax)), 0);
    }
    dispatch(extendedFieldActions.setMax(newValue));
  };

  return [max, setMax];
};

const useChangeCountry = () => {
  const [{ configuration }, dispatch] = useContext(ExtendedFieldsContext);
  const changeCountry = Boolean(configuration?.subfields?.country?.isVisible);

  const setChangeCountry = (newValue) =>
    dispatch(extendedFieldActions.setChangeCountry(newValue));

  return [changeCountry, setChangeCountry];
};

const useDefaultCountry = () => {
  const [{ configuration }, dispatch] = useContext(ExtendedFieldsContext);
  const defaultCountry = configuration?.responseOptions?.default?.country;

  const setDefaultCountry = (newValue) =>
    dispatch(extendedFieldActions.setDefaultCountry(newValue));

  return [defaultCountry, setDefaultCountry];
};

const useIncludeYear = () => {
  const [{ configuration }, dispatch] = useContext(ExtendedFieldsContext);
  const includeYear = Boolean(configuration?.subfields?.year?.isVisible);

  const setIncludeYear = (newValue) =>
    dispatch(extendedFieldActions.setIncludeYear(newValue));

  return [includeYear, setIncludeYear];
};

const useOptions = () => {
  const [{ configuration }, dispatch] = useContext(ExtendedFieldsContext);
  const options = useMemo(
    () =>
      configuration?.responseOptions?.responseOptionList?.map((elem) =>
        // assign an id to each from optionListValueID. If the option is new, it should be assigned from FnrCreateOptions.
        ({
          id: elem.id || elem.optionListValueID,
          displayValue: elem.displayValue || elem.optionListValue,
          ...elem, // retain any data given by the api.
        })
      ) || [],
    [configuration?.responseOptions?.responseOptionList]
  );

  const setOptions = useCallback(
    (newValue = []) => {
      dispatch(extendedFieldActions.setOptions(newValue));
    },
    [dispatch]
  );
  return [options, setOptions];
};

const useMultiOptions = () => {
  const [{ configuration }, dispatch] = useContext(ExtendedFieldsContext);
  const multiOptions = Boolean(configuration?.behaviorOptions?.allowMultiple);

  const setMultiOptions = (newValue) =>
    dispatch(extendedFieldActions.setMultiOptions(newValue));

  return [multiOptions, setMultiOptions];
};

const useBlockSharing = () => {
  const [{ blockSharing }] = useContext(ExtendedFieldsContext);
  const isBlocked = Boolean(Number(blockSharing));

  // This is read only, thus there is no setter.

  return [isBlocked];
};

const useShowIIG = () => {
  const [fieldType] = useSelectedFieldType();

  const show = useMemo(() => {
    switch (fieldType) {
      case fieldTypes.fileUpload:
        return false;
      default:
        return true;
    }
  }, [fieldType]);

  // This is read only, thus there is no setter.
  return [show];
};

const useErrorMessage = () => {
  const [{ errorMessage }, dispatch] = useContext(ExtendedFieldsContext);
  const setErrorMessage = useCallback(
    (newErrorMessage) => {
      dispatch(extendedFieldActions.setErrorMessage(newErrorMessage));
    },
    [dispatch]
  );
  return [errorMessage, setErrorMessage];
};

/**
 * Determines whether or not the navigation panel should always be displayed
 * @returns {[boolean]}
 */
const useDisplayIsFullWidth = () => {
  const [{ displayIsFullWidth }, dispatch] = useContext(ExtendedFieldsContext);

  const setDisplayIsFullWidth = useCallback(
    (newFullWidth) => {
      dispatch(extendedFieldActions.setDisplayIsFullWidth(newFullWidth));
    },
    [dispatch]
  );

  return [displayIsFullWidth, setDisplayIsFullWidth];
};

/**
 * Determines whether or not a field is being edited (new or existing)
 * @returns {[boolean]}
 */
const useIsEditing = () => {
  const [isCustom] = useSelectedIsCustom();
  const [networkFieldID] = useSelectedFieldId();

  return [Boolean(networkFieldID) || isCustom];
};

// In case you need a string for a switch, this is available too.
const useVariant = () => {
  const [{ groupId }] = useContext(ExtendedFieldsContext);
  const variant = groupId ? "profile" : "signup";
  return variant;
};

/**
 * Returns whether or this the UI is being used for a group (profiles)
 * @returns {[boolean]}
 */
const useIsForGroup = () => {
  const [{ groupId, signupItemId }] = useContext(ExtendedFieldsContext);
  return [Boolean(groupId) && !signupItemId];
};

/**
 * Returns whether or this the UI is being used for a signupItem
 * @returns {[boolean]}
 */
const useIsForSignupItem = () => {
  const [{ groupId, signupItemId }] = useContext(ExtendedFieldsContext);
  return [Boolean(signupItemId) && !groupId];
};

const useExternalNavigationHooks = () => {
  const [{ externalNavigationHooks }] = useContext(ExtendedFieldsContext);

  return externalNavigationHooks;
};

const useHasAvailableFields = () => {
  const [state] = useContext(ExtendedFieldsContext);
  const available = state?.extendedFields?.availableFields;
  const hasProfileFields = available?.profiles?.length > 0;
  const thisSignupHasFields = available?.thisSignup?.length > 0;
  const otherSignupsHaveFields = available?.otherSignups?.length > 0;
  return hasProfileFields || thisSignupHasFields || otherSignupsHaveFields;
};

/**
 * Returns whether or this the UI is should edit fields with a modal
 * @returns {[boolean]}
 */
const useEditWithModal = () => {
  const [{ editWithModal }, dispatch] = useContext(ExtendedFieldsContext);
  const setEditWithModal = useCallback(
    (newEditWithModal) => {
      dispatch(extendedFieldActions.setEditWithModal(newEditWithModal));
    },
    [dispatch]
  );

  return [editWithModal, setEditWithModal];
};

const useCustomData = () => extendedFieldsCustomFieldData;

const extendedFieldHooks = {
  useSelectedFieldId,
  useHasExtendedFields,
  useFieldData,
  useFlatFields,
  useSelectedFieldType,
  useSelectedIsCustom,
  useSelectedIsAnotherGroup,
  useClearSelection,
  useGroupId,
  useSignupItemId,
  useIsEveryoneGroup,
  useSelectedIsEnabledInOtherGroups,
  useShowActiveOtherGroups,
  useSyncSelectedField,
  useInitExtendedFields,
  useUpdateExtendedFieldOrder,
  useSubmitField,
  useDeleteExtendedField,
  useFieldDragItems,
  useDefaultFields,
  useFieldSelectData,
  useIsSuperAdmin,
  useIsReadOnly,
  useOtherGroupFields,
  useIcon,
  useTitle,
  useIsRequiredField,
  useGroupName,
  useEditPermissionId,
  useViewPermissionId,
  useShowOnSignUp,
  useIig,
  usePeriodicIig,
  useShareWith,
  useMultiline,
  useMin,
  useMax,
  useChangeCountry,
  useDefaultCountry,
  useIncludeYear,
  useOptions,
  useMultiOptions,
  useIsNavigating,
  useBlockSharing,
  useIsNewField,
  useSelectedIsFromProfiles,
  useErrorMessage,
  useDisplayIsFullWidth,
  useIsEditing,
  useVariant,
  useIsForGroup,
  useIsForSignupItem,
  useExternalNavigationHooks,
  useHasAvailableFields,
  useEditWithModal,
  useCustomData,
  useShowIIG,
  useIsUsedBySmartGroup,
  useNotDeletable,
};

export default extendedFieldHooks;
